import axios from "axios";
import { getCookieValue } from "./helpers";
import {
  BASE_URL,
  COUNTRY_CODE,
  WHITELABEL_EDGE_ID,
  WHITELABEL_EDGE_TOKEN,
} from "./constants";
import { WhitelabelConfig } from "@types";
import { Revalidator, RevalidatorOptions } from "swr";

export async function fetcher(url: string) {
  const websiteUid = getCookieValue("itk-website-uid");
  const locale = getCookieValue("NEXT_LOCALE");
  const instance = axios.create({
    baseURL: `${BASE_URL}/legacy/${COUNTRY_CODE}/shop/`,
    headers: {
      "content-language": locale,
      "itk-website-uid": websiteUid,
    },
  });

  return instance
    .get(url)
    .then((res) => res.data)
    .catch((err) => {
      throw err;
    });
}

export async function authorizedFetcher([url, token]: [string, string]) {
  const websiteUid = getCookieValue("itk-website-uid");
  const locale = getCookieValue("NEXT_LOCALE");
  const instance = axios.create({
    baseURL: `${BASE_URL}/legacy/${COUNTRY_CODE}/shop/`,
    headers: {
      Authorization: `Bearer ${token}`,
      "content-language": locale,
      "itk-website-uid": websiteUid,
    },
  });

  return instance
    .get(url)
    .then((res) => res.data)
    .catch((err) => {
      throw err;
    });
}

export const getEdgeItem = async (key: string) =>
  await fetch(
    `https://edge-config.vercel.com/${process.env.EDGE_ID}/item/${key}?token=${process.env.EDGE_TOKEN}`
  )
    .then((res) => res.json())
    .catch((err) => {
      throw err;
    });

export const getWLEdgeItem = async (key: string): Promise<WhitelabelConfig> =>
  await fetch(
    `https://edge-config.vercel.com/${WHITELABEL_EDGE_ID}/item/${key.replace(
      ".",
      "-"
    )}?token=${WHITELABEL_EDGE_TOKEN}`,
    {
      next: {
        revalidate: 60,
      },
    }
  )
    .then((res) => res.json())
    .catch((err) => {
      throw err;
    });

export const unPendOrder = (basketKey: string | undefined) =>
  axios.patch(
    `${BASE_URL}/legacy/${COUNTRY_CODE}/shop/basket/${basketKey}/checkout/unpend`
  );

export async function basketSummaryFetcher(url: string) {
  const websiteUid = getCookieValue("itk-website-uid");
  const locale = getCookieValue("NEXT_LOCALE");
  return axios
    .get(`${BASE_URL}/legacy/${COUNTRY_CODE}/shop/${url}`, {
      headers: {
        "content-language": locale,
        "itk-website-uid": websiteUid,
      },
    })
    .then(async (res) => {
      const { data } = res;

      if (!data) {
        await axios.delete("/api/basket");
        return null;
      }

      if (data.Ttl <= 0) {
        const basketKey = getCookieValue("ASP.NET_SessionId");
        const res = await fetch(
          `${BASE_URL}/legacy/${COUNTRY_CODE}/shop/basket/${basketKey}`,
          {
            method: "DELETE",
            headers: {
              "itk-website-uid": websiteUid ?? "",
            },
          }
        );
        if (res.status === 200) {
          await axios.delete("/api/basket");
        }
        return null;
      }

      return data;
    })
    .catch((err) => {
      throw err;
    });
}

export function handle429s(
  error: any,
  _: string,
  __: any,
  revalidate: Revalidator,
  { retryCount }: Required<RevalidatorOptions>
) {
  if (error.response.status !== 429) return;
  const retry_after = error.response.headers["retry-after"]
    ? Math.max(parseInt(error.response.headers["retry-after"]), 1)
    : error.response.headers["rl-twm"]
    ? Math.round(error.response.headers["rl-twm"] * 60)
    : 5;
  setTimeout(() => revalidate({ retryCount }), retry_after * 1000);
}
